import { useEffect, useState, useSyncExternalStore } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import {
	getForgePageBannerHeight,
	getServerForgePageBannerHeight,
	subscribeToForgePageBannerHeight,
} from './forgePageBannerHeightStore.ts';

const subscribeWithoutFixedBanner = () => () => {};

export const useForgePageBannerListener = (): {
	isForgePageBannerFixed: boolean;
	forgePageBannerHeight: number;
} => {
	// Keep the control's eager read, but never perform it in treatment.
	const [legacyHeight, setLegacyHeight] = useState(
		expVal('cc_forge_banner_height_snapshot', 'isEnabled', false) ? 0 : getForgeAppsHeight(),
	);
	const isLivePage = useIsCurrentPageLive();

	const isForgePageBannerFixed = !isLivePage;

	useEffect(() => {
		if (expVal('cc_forge_banner_height_snapshot', 'isEnabled', false)) {
			return;
		}
		const listener = (e) => {
			if (expVal('confluence_pgxp_p2m_batch_1', 'isEnabled', false)) {
				setLegacyHeight(e?.detail?.height ?? 0);
			} else {
				setLegacyHeight(getForgeAppsHeight());
			}
		};
		if (isForgePageBannerFixed) {
			document.addEventListener('forgePageBannerHeightUpdated', listener);
		}
		return () => {
			if (isForgePageBannerFixed) {
				document.removeEventListener('forgePageBannerHeightUpdated', listener);
			}
		};
	}, [isForgePageBannerFixed]);

	const forgeAppsHeight = useSyncExternalStore(
		isForgePageBannerFixed && expVal('cc_forge_banner_height_snapshot', 'isEnabled', false)
			? subscribeToForgePageBannerHeight
			: subscribeWithoutFixedBanner,
		isForgePageBannerFixed && expVal('cc_forge_banner_height_snapshot', 'isEnabled', false)
			? getForgePageBannerHeight
			: getServerForgePageBannerHeight,
		getServerForgePageBannerHeight,
	);

	return {
		isForgePageBannerFixed,
		forgePageBannerHeight: expVal('cc_forge_banner_height_snapshot', 'isEnabled', false)
			? forgeAppsHeight
			: legacyHeight,
	};
};

const getForgeAppsHeight = () => {
	if (typeof document === 'undefined') {
		return 0;
	}

	return (
		parseInt(
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			getComputedStyle(document.documentElement).getPropertyValue('--forge-page-banner-height'),
		) || 0
	);
};
